/**
 * One region's verdict.
 *
 * The card has two shapes, and which one it takes is the point.
 *
 * When the region has no entry at all, or its status is `unknown`, it shows no
 * numbers. There is nothing to show: nobody measured it, and printing "%0
 * Kapsama" would state a measurement that was never taken - the same error as
 * calling an unweighed parcel weightless. The card says so and stops.
 *
 * When the region was measured, it shows Kapsama and Güven as two separately
 * labelled figures, the independent-viewpoint count, and - if this is not the
 * first coverage run - what changed since the previous one.
 */

import type { CoverageRegion, ProjectRegionChange, ProjectRegionCoverage } from "@/api/types";
import { Meter, StatusBadge } from "@/components/ui";
import {
  COVERAGE_REGION_LABELS,
  COVERAGE_STATUS_TERMS,
  METRIC_HELP,
  METRIC_LABELS,
  REGION_NEXT_ACTION,
} from "@/i18n/terms";
import { formatScore, formatViews } from "@/lib/format";
import "./coverage.css";

export function RegionCard({
  region,
  data,
  change,
}: {
  region: CoverageRegion;
  data: ProjectRegionCoverage | null;
  change: ProjectRegionChange | null;
}) {
  const name = COVERAGE_REGION_LABELS[region];

  if (data === null) {
    return (
      <article className="card regioncard regioncard--neutral">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h4 className="regioncard__name">{name}</h4>
          <StatusBadge tone="neutral">{COVERAGE_STATUS_TERMS.unknown.label}</StatusBadge>
        </div>
        <p className="muted">{COVERAGE_STATUS_TERMS.unknown.meaning}</p>
        <p className="faint">{REGION_NEXT_ACTION.unknown}</p>
      </article>
    );
  }

  const term = COVERAGE_STATUS_TERMS[data.status];
  const unknown = data.status === "unknown";

  return (
    <article className={`card regioncard regioncard--${term.tone}`}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h4 className="regioncard__name">{name}</h4>
        <StatusBadge tone={term.tone}>{term.label}</StatusBadge>
      </div>

      <p className="muted">{term.meaning}</p>

      {unknown ? (
        // No numbers for an unmeasured region. The reason, when the backend
        // gives one, is far more useful than a zero would be.
        data.unmeasurable_reason && <p className="faint">{data.unmeasurable_reason}</p>
      ) : (
        <>
          <div className="regioncard__metric">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="regioncard__metric-label" title={METRIC_HELP.coverage}>
                {METRIC_LABELS.coverage}
              </span>
              <span className="regioncard__metric-value">{formatScore(data.coverage_score)}</span>
            </div>
            <Meter
              value={data.coverage_score}
              tone={term.tone}
              label={METRIC_LABELS.coverage}
              formatted={formatScore(data.coverage_score)}
            />
          </div>

          <div className="regioncard__metric">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="regioncard__metric-label" title={METRIC_HELP.confidence}>
                {METRIC_LABELS.confidence}
              </span>
              <span className="regioncard__metric-value">
                {formatScore(data.coverage_confidence)}
              </span>
            </div>
            <Meter
              value={data.coverage_confidence}
              tone="neutral"
              label={METRIC_LABELS.confidence}
              formatted={formatScore(data.coverage_confidence)}
            />
          </div>

          <dl className="regioncard__facts">
            <div>
              <dt title={METRIC_HELP.independentViews}>{METRIC_LABELS.independentViews}</dt>
              <dd>{formatViews(data.independent_views)}</dd>
            </div>
            <div>
              <dt title={METRIC_HELP.sourceSessions}>{METRIC_LABELS.sourceSessions}</dt>
              <dd>{data.source_session_count}</dd>
            </div>
            {data.best_session_score !== null && data.best_session_score !== undefined && (
              <div>
                <dt title={METRIC_HELP.bestSession}>{METRIC_LABELS.bestSession}</dt>
                <dd>{formatScore(data.best_session_score)}</dd>
              </div>
            )}
          </dl>
        </>
      )}

      {/* What to do next, and it is not the same sentence for every status.
          An unmeasured region needs a capture the analysis can read; a
          measured-but-short one needs more angles. Telling someone to
          photograph a side they already photographed - because the landmark
          model could not read it - is the error this separation prevents. */}
      {REGION_NEXT_ACTION[data.status] && (
        <p className="faint">{REGION_NEXT_ACTION[data.status]}</p>
      )}

      {change && <ChangeNote change={change} />}
    </article>
  );
}

/**
 * What the last run changed for this region.
 *
 * "Değişmedi" is shown rather than hidden. After a correction session the most
 * important thing a user can learn is that their extra capture achieved
 * nothing - silence would read as success.
 */
function ChangeNote({ change }: { change: ProjectRegionChange }) {
  if (!change.status_changed && Math.abs(change.score_delta) < 0.005) {
    return <p className="regioncard__change faint">Önceki hesaplamaya göre değişmedi.</p>;
  }

  const sign = change.score_delta > 0 ? "+" : "−";
  const magnitude = formatScore(Math.abs(change.score_delta));
  const tone = change.improved ? "success" : "danger";

  return (
    <p className={`regioncard__change regioncard__change--${tone}`}>
      {change.status_changed
        ? `${COVERAGE_STATUS_TERMS[change.before_status].label} → ${COVERAGE_STATUS_TERMS[change.after_status].label}`
        : "Kapsama"}{" "}
      ({sign}
      {magnitude})
    </p>
  );
}
